<template>
  <span>
    <PatientCallingModal :patients="patient"
                         ref="patientCallingModal"
                         :phone="phone"
                         :leadId="leadId"/>
    <ApproveTechSupportLoginModal :superUserRequestingAccessId="superUserRequestingAccessId"
                                  :userIdToImitateLogin="userIdToImitateLogin"/>
  </span>
</template>

<script setup>
import JwtService from "@/services/jwt.service"
import { useStore } from "vuex"
import PatientCallingModal from "@/components/parts/crm/PatientCallingModal"
import initEcho from "@/extensions/plugins/echo"
import ApproveTechSupportLoginModal from "@/components/parts/settings/ApproveTechSupportLoginModal.vue"
import {computed, getCurrentInstance, nextTick, onMounted, ref} from "vue"
import { playNotySound } from "@/services/sound.service"

const store = useStore()

// let i18n = null
let noty = null
let bvModal = null

// const useProxy = computed(() => store.state.auth.useProxy)
const clinic = computed(() => store.state.auth.clinic)
const user = computed(() => store.state.auth.user)

const binotelUsers = computed(() => store.getters.dynamicConfigValue('binotel') || [])
const sipuniUsers = computed(() => store.getters.dynamicConfigValue('sipuni') || [])
const uisUsers = computed(() => store.getters.dynamicConfigValue('uis') || [])
const voipUsers = computed(() => store.getters.dynamicConfigValue('voipUsers') || [])
const pbxUsers = computed(() => store.getters.dynamicConfigValue('pbxUsers') || [])
const vendorUsers = computed(() => {
  if(vendor.value === 'binotel') return binotelUsers.value
  if(vendor.value === 'sipuni') return sipuniUsers.value
  if(vendor.value === 'uis') return uisUsers.value
  if(vendor.value === 'voip') return voipUsers.value
  if(vendor.value === 'pbx') return pbxUsers.value
  return []
})
const vendorUserIds = computed(() => vendorUsers.value.map(u => u?.id ?? u))

const patient = ref(null)
const phone = ref(null)
const leadId = ref(null)
const vendor = ref(null)
const echo = ref(null)
const patientCallingModal = ref(null)
const superUserRequestingAccessId = ref(null)
const userIdToImitateLogin = ref(null)

const processMessage = async (data) => {
  if(!data || !data.message || !data.message.type) return
  if(data.message.type === 'refreshCalendar') {
    store.commit('setRefreshCalendar', true)
    return
  }
  if(data.message.type === 'trustMeStatus') {
    store.commit('incrementDataTableCounter')
    return
  }
  if(data.message.type === 'planTreatmentSession') {
    store.commit('setRefreshPlanSessions', true)
    return
  }
  if(data.message.type === 'planTreatmentChanged') {
    store.commit('setPlanTreatmentChanged', data.message.planId)
    return
  }
  if(data.message.type === 'planTreatmentVisitChanged') {
    store.commit('setPlanTreatmentVisitChanged', data.message.visitId)
    return
  }
  vendor.value = data.message.provider
  if(!vendorUserIds.value.includes(user.value.id)) return
  if(data.message.type === 'callStarted') {
    const internalPhone = data.message.internalPhone
    if(internalPhone) {
      const usersWithThisInternal = vendorUsers.value.filter(u => (u?.tel ?? null) === internalPhone)
      if(usersWithThisInternal.length) {
        const userIds = usersWithThisInternal.map(u => u.id)
        if(!userIds.includes(user.value.id)) return // means it is addressed to specific user(s)
      }
    }
    const employeeId = data.message.employeeId
    if(employeeId) {
      const usersWithThisEmployeeId = vendorUsers.value.filter(u => (u?.employeeId ?? null) === employeeId)
      if(usersWithThisEmployeeId.length) {
        const userIds = usersWithThisEmployeeId.map(u => u.id)
        if(!userIds.includes(user.value.id)) return // means it is addressed to specific user(s)
      }
    }
    patient.value = data.message.patient
    phone.value = data.message.phone
    leadId.value = data.message.leadId || null
    store.commit('setShowCallModal', true)
    await nextTick()
    patientCallingModal.value?.show()
  }
  if(data.message.type === 'callEnded' && data.message.responded) {
    store.commit('setShowCallModal', false)
    vendor.value = null
  }
  if(data.message.type === 'playSound') {
    playNotySound()
    updatePanelCounter(data?.message?.vendor)
  }
}

const updatePanelCounter = (vendor) => {
  if(!store.state.dom.rightPaneOpened) {
    if(vendor === 'jetApi') store.state.crm.newPanelJetApiNotifications++
    if(vendor === 'greenApi') store.state.crm.newPanelGreenApiNotifications++
    if(vendor === 'aura') store.state.crm.newPanelAuraNotifications++
  }
}

const processUserMessage = async (data) => {
  if(data.message.type === 'visitTiming') {
    store.commit('setMyOpenedVisits', data.message.visits)
  }
  if(data.message.type === 'accessRequest') {
    superUserRequestingAccessId.value = data.message.data.superUserId
    userIdToImitateLogin.value = data.message.data.userIdToImitateLogin
    bvModal.show('approve-tech-support-login-modal')
  }
  if(data.message.type === 'accessRequestFinished') {
    bvModal.hide('approve-tech-support-login-modal')
  }
  if(data.message.type === 'privateMessage' && Number(data.user_id) === Number(user.value.id)) {
    noty.info(data.message.message)
    playNotySound()
    store.commit('setHeaderCounter', { counter: 'excels', value: 1 } )
    await nextTick()
    bvModal.show('excel-reports-modal')
  }
  if(data.message.type === 'playSound') {
    playNotySound()
    updatePanelCounter(data?.message?.vendor)
  }
}

onMounted(async () => {
  const instance = (getCurrentInstance())
  bvModal = instance?.ctx?._bv__modal
  // i18n = instance?.ctx?.$i18n
  noty = instance?.appContext?.app?.noty

  echo.value = initEcho(false)
  echo.value.options.auth.headers.Authorization = 'Bearer '+JwtService.getToken()

  echo.value.private(`clinic.${clinic.value.id}`)
      .listen('.messageSent', (data) => {
        console.log(data)
        processMessage(data)
      }).error(err => console.log(err))

  echo.value.private(`user.${user.value.id}`)
      .listen('.userMessageSent', (data) => {
        console.log('userMessageSent', data)
        processUserMessage(data)
      }).error(err => console.log(err))
})
</script>
